import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Stack,
  Card,
  Grid,
  Chip,
} from "@material-ui/core";
import { LoadingButton } from "@mui/lab";
import { Icon } from "@iconify/react";
import editIcon from "@iconify-icons/eva/edit-2-outline";
import checkCircle from "@iconify-icons/eva/checkmark-circle-2-fill";
import { useDispatch, useSelector } from "src/redux/store";
import { getProducts } from "src/redux/slices/product";
import { getCategories } from "src/redux/slices/categories";
import BulkProductEditor from "src/views/app/UserNormal/products/BulkProductEditor";
import { getPendingCartaScanJobId } from "src/components/products/ClaudeScanCartaDialog";

// ----------------------------------------------------------------------
//   Wizard · Step Carta con IA. Antes tenía una versión simplificada
//   de escaneo + revisión rápida. Ahora monta el `BulkProductEditor`
//   completo (el mismo que se usa en el portal desde /productos), que
//   permite:
//     · Escaneo IA de carta (F77 · fotos/PDF → productos + categorías).
//     · Crear/editar/borrar productos.
//     · Editar complementos, alérgenos, IVA, impresora individual.
//     · Aplicar plantilla de complementos a N productos.
//     · Crear categorías y productos inline sin salir del editor.
//     · Preview cliente en vivo.
//
//   El editor se abre como Dialog encima del wizard. Cerrar con
//   guardado (`onClose(true)`) marca el step como completado
//   automáticamente. Cerrar sin guardar (`onClose(false)`) NO avanza;
//   el user puede saltarlo desde el nav inferior si prefiere hacerlo
//   más tarde desde el portal.
// ----------------------------------------------------------------------

export default function StepCarta({ onComplete, onSkip }) {
  const dispatch = useDispatch();
  const { products } = useSelector((s) => s.product);
  const { categories } = useSelector((s) => s.categories);
  const [editorOpen, setEditorOpen] = useState(false);
  // Cuando el user abre el editor desde el CTA principal del wizard,
  // saltamos directo al modal de "sube tu carta con IA" — antes se
  // abría la vista lista y el user se perdía teniendo que buscar el
  // botón "Escanear con IA". Si abre desde el CTA secundario "editar
  // manualmente", el flag queda false y va a la vista lista.
  const [openScanFirst, setOpenScanFirst] = useState(false);

  // Cargamos catálogo real para que el editor arranque con datos y
  // permita reutilizar productos como complementos, plantillas, etc.
  useEffect(() => {
    if (!products || products.length === 0) dispatch(getProducts(true));
    if (!categories || categories.length === 0) dispatch(getCategories());
  }, [dispatch, products, categories]);

  // Si el usuario ya inició un escaneo Claude en cualquier vista y
  // acaba en el wizard (relogin / reinicio / navegación cruzada),
  // el jobId sigue vivo en localStorage → abrimos el editor
  // automáticamente para que su `ClaudeScanCartaDialog` interno
  // detecte el pending y muestre el progreso en vivo sin perder
  // los eventos SignalR ni obligar a re-subir imágenes.
  useEffect(() => {
    if (getPendingCartaScanJobId()) setEditorOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const productCount = (products || []).length;
  const categoryCount = (categories || []).length;
  const hasCatalog = productCount > 0;

  const handleEditorClose = (saved) => {
    setEditorOpen(false);
    setOpenScanFirst(false);
    if (saved) {
      // Guardado exitoso → refrescamos catálogo local y marcamos el
      // step completado. El wizard salta al siguiente step.
      dispatch(getProducts(true));
      dispatch(getCategories());
      onComplete?.({
        dataJson: JSON.stringify({
          savedProductCount: productCount,
          savedCategoryCount: categoryCount,
        }),
      });
    }
    // Si el user cerró sin guardar, NO avanzamos — que decida usar el
    // botón "Saltar este paso" del nav inferior si prefiere posponerlo.
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        Tu carta con IA
      </Typography>
      <Typography sx={{ color: "text.secondary", mb: 3 }}>
        Sube una foto o PDF de tu carta y la IA te la digitaliza en segundos.
        También puedes crear categorías y productos a mano, editar
        complementos, alérgenos y precios — todo lo que puedes hacer desde
        Productos del portal, dentro del mismo editor.
      </Typography>

      {!hasCatalog && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Todavía no tienes productos. Abre el editor para escanear tu carta
          o crearlos a mano. Puedes saltar este paso y hacerlo después desde
          el menú <strong>Productos</strong> del portal.
        </Alert>
      )}

      {hasCatalog && (
        <Card sx={{ p: 3, mb: 3, bgcolor: "success.lighter" }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Icon icon={checkCircle} width={32} style={{ color: "#16a34a" }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Ya tienes carta cargada
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                <Chip
                  size="small"
                  label={`${productCount} producto${productCount === 1 ? "" : "s"}`}
                  color="success"
                  sx={{ fontWeight: 700 }}
                />
                <Chip
                  size="small"
                  label={`${categoryCount} categoría${categoryCount === 1 ? "" : "s"}`}
                  color="success"
                  variant="outlined"
                  sx={{ fontWeight: 700 }}
                />
              </Stack>
            </Box>
          </Stack>
        </Card>
      )}

      {/* CTA principal — escaneo IA directo. Botón secundario para
          entrar al editor manual sin pasar por el scan. */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Card
            onClick={() => {
              setOpenScanFirst(true);
              setEditorOpen(true);
            }}
            sx={{
              p: 4,
              textAlign: "center",
              cursor: "pointer",
              border: "2px dashed",
              borderColor: "primary.light",
              transition: "all 0.15s",
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: "primary.lighter",
                transform: "translateY(-2px)",
              },
            }}
          >
            <Icon
              icon={editIcon}
              width={48}
              style={{ color: "#1976d2", marginBottom: 8 }}
            />
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
              Escanear mi carta con IA
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              Sube una foto o PDF de tu carta y la IA digitaliza productos,
              categorías y precios en segundos.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={(e) => {
                e.stopPropagation();
                setOpenScanFirst(true);
                setEditorOpen(true);
              }}
            >
              Escanear con IA
            </Button>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Button
            variant="text"
            size="small"
            onClick={() => {
              setOpenScanFirst(false);
              setEditorOpen(true);
            }}
            sx={{ textTransform: "none" }}
          >
            {hasCatalog ? "Editar carta manualmente" : "Crear productos a mano"}
          </Button>
        </Grid>
      </Grid>

      <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
        <Button color="inherit" onClick={onSkip}>
          Configurar después
        </Button>
        {hasCatalog && (
          <LoadingButton
            variant="contained"
            onClick={() =>
              onComplete?.({
                dataJson: JSON.stringify({
                  productCount,
                  categoryCount,
                  skippedEditor: true,
                }),
              })
            }
            sx={{ minWidth: 180 }}
          >
            Continuar así
          </LoadingButton>
        )}
      </Stack>

      {/* Editor completo — se abre encima del wizard modal. Al cerrar con
          guardado marca el step automáticamente. */}
      <BulkProductEditor
        open={editorOpen}
        initialRows={products || []}
        categoriesAvailable={categories || []}
        productsAvailable={products || []}
        title="Editor de carta"
        subtitle="Sube tu carta con IA o crea productos manualmente · Complementos, alérgenos y plantillas"
        onClose={handleEditorClose}
        autoOpenScanOnMount={openScanFirst}
      />
    </Box>
  );
}
